import { useQuery } from '@tanstack/react-query';
import { ApiError, apiFetch, apiFetchJson } from './api.ts';
import { fetchCurrentUser } from './auth.ts';

/**
 * Whether a staff member is signed in as this person.
 *
 * Read from the session rather than from `/users/me`, because that is what it
 * is: a property of how this browser is signed in, not of the person it is
 * signed in as. The user record is the same object for every caller and every
 * endpoint that returns one, and a session-only field has no business widening
 * it.
 */
export async function fetchImpersonatedBy(): Promise<string | null> {
  const session = await apiFetchJson<{ impersonated_by?: string | null } | null>(
    '/auth/get-session',
  );

  return session?.impersonated_by ?? null;
}

/**
 * The banner, the disabled composer and anything else that has to know all read
 * this one query, so they agree and it is fetched once.
 *
 * `undefined` while it is still being read, which is not the same as "no": the
 * banner stays off either way, but the identity watch must not read a session
 * it has not seen yet as a session that changed.
 *
 * It refetches when the window regains focus, like every other query here. That
 * is what makes a tab notice that impersonation started or stopped somewhere
 * else — the session cookie is shared by the whole browser.
 */
/**
 * Hand the session back to the staff member behind it.
 *
 * The endpoint deletes the impersonated session and restores the admin's own
 * from the cookie better-auth stashed it in. What follows must be a full
 * navigation, not a re-render — the caller decides where to.
 */
export async function stopImpersonation(): Promise<void> {
  try {
    await apiFetch('/auth/admin/stop-impersonating', { method: 'POST' });
  } catch (error) {
    /* A 400 means it was already stopped, e.g. in another tab: the admin has their session back. */
    if (error instanceof ApiError && error.status === 400) return;
    throw error;
  }
}

/**
 * A superadmin working in a customer organisation through their own guest
 * account, rather than "login as" on somebody else. It may act — chat, speak,
 * practise — where a borrowed session may only look. The backend decides the
 * same way; this only keeps the screens from offering what it would refuse.
 */
export function isGuestVisit(
  user: { guest_of_user_id?: string | null } | undefined,
  impersonatedBy: string | null | undefined,
): boolean {
  return !!impersonatedBy && user?.guest_of_user_id === impersonatedBy;
}

export function useImpersonatedBy(): string | null | undefined {
  const { data } = useQuery({
    queryKey: ['impersonatedBy'],
    queryFn: fetchImpersonatedBy,
    /*
     * Never held: the rest of the app keeps an answer for half a minute, and
     * half a minute is exactly how long this tab would go on being somebody
     * else after staff stopped impersonating in another one.
     */
    staleTime: 0,
  });

  return data;
}

/**
 * Staff signed in as somebody else, which may only look. A guest visit is the
 * superadmin themselves, so it is not borrowed and may write.
 */
export function useBorrowedSession(): boolean {
  const impersonatedBy = useImpersonatedBy();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  return !!impersonatedBy && !isGuestVisit(user, impersonatedBy);
}
